import React, { Dispatch, SetStateAction, SyntheticEvent, useEffect, useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
} from "@mui/material";
import Button from "@mui/material/Button";
import { SiteDTO, SiteTypes } from "@beable/roster-backend-client-v1";
import { useTheme } from "@mui/material/styles";
import { getStyles } from "../styles";
import {
  useCustomerDispatch,
  CustomerActionType,
} from "../../../context/CustomerContext";
import MuiAlert from '@mui/material/Alert';
import managementService from "../../../services/AdminManagementService";

interface SiteTypeManagementProps {
  site: SiteDTO;
  showMessage: (msg: string, severity: "error" | "info" | "success" | "warning") => void;
  closeMessage: () => void;
  messageSettings: {
    open: boolean;
    message: string;
    severity: 'error' | 'info' | 'success' | 'warning';
  };
  setSite: Dispatch<SetStateAction<SiteDTO>>;
}

export const SiteTypeManagement = ({
  site,
  showMessage,
  closeMessage,
  messageSettings,
  setSite
}: SiteTypeManagementProps) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [newSiteType, setNewSiteType] = useState<SiteTypes>(
    site.type ?? SiteTypes.demo
  );
  const customerDispatch: any = useCustomerDispatch();

  useEffect(() => {
    setNewSiteType(site.type ?? SiteTypes.demo);
  }, [site]);

  const handleSiteTypeChange = async (e: any) => {
    const newType = e.target.value;
    setNewSiteType(newType);
  };

  const submitSiteTypeChange = async (e: any) => {
    if (site && site.type !== newSiteType) {
      const updateResp = await managementService.changeSiteType(
        site.id,
        newSiteType
      );
      if (updateResp) {
        showMessage("Site type changed successfully!", "success");
        customerDispatch({
          type: CustomerActionType.SiteChanged,
          site: { ...site, type: newSiteType },
        });
        setSite({ ...site, type: newSiteType })
      } else {
        showMessage("Could not save the new type...", "error");
      }
    }
  };

  return <>
    <FormControl
      fullWidth
      variant="outlined"
      sx={styles.pFormControl}
    >
      <InputLabel id="siteType-label">Select Site Type</InputLabel>
      <Select
        variant="standard"
        labelId="siteType-label"
        id="siteTypeSelect"
        value={newSiteType}
        label="Select Site Type"
        onChange={handleSiteTypeChange}
        data-testid="siteTypeSelector">
        <MenuItem value={SiteTypes.qa} role="option">
          {SiteTypes.qa}
        </MenuItem>
        <MenuItem value={SiteTypes.demo} role="option">
          {SiteTypes.demo}
        </MenuItem>
        <MenuItem value={SiteTypes.customer} role="option">
          {SiteTypes.customer}
        </MenuItem>
        <MenuItem value={SiteTypes.friends} role="option">
          {SiteTypes.friends}
        </MenuItem>
        <MenuItem value={SiteTypes.employee} role="option">
          {SiteTypes.employee}
        </MenuItem>
        <MenuItem value={SiteTypes.presenter} role="option">
          {SiteTypes.presenter}
        </MenuItem>
      </Select>
    </FormControl>
    <Box sx={styles.rightAlign}>
      <Button
        variant="contained"
        color="primary"
        id="saveSiteType"
        onClick={submitSiteTypeChange}
      >
        Save
      </Button>
    </Box>
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      open={messageSettings.open}
      autoHideDuration={5000}
      onClose={closeMessage}
    >
      <MuiAlert
        onClose={closeMessage}
        severity={messageSettings.severity}
        elevation={6}
        variant="filled"
      >
        {messageSettings.message}
      </MuiAlert>
    </Snackbar>
  </>;
};
